import {FC, useMemo} from 'react'
import {ColumnDef, ColumnFiltersState} from '@tanstack/react-table'
import {CSVLink} from 'react-csv'
import {Button, Card, OverlayTrigger, Tooltip} from 'react-bootstrap'
import DataTable from '../../../../../ui-kit/DataTable'
import {formatPercentageNumber, getNameByISO3, shortenLargeNumber} from '../../../../libs/misc'
import {AffectedFlowRow, GroupDetail, SimulationResponse} from '../core/_models'
import {buildAffectedFlowRows, decodeTariffCode, lossTooltip} from '../core/_transforms'

type Props = {
  result: SimulationResponse
  groupDetails: Record<string, GroupDetail>
  reporter: string
  filterEcon: string
}

const tariffCell = (
  value: number,
  code: string,
  econ: string,
  groups: Record<string, GroupDetail>
) => (
  <OverlayTrigger overlay={<Tooltip>{decodeTariffCode(code, econ, groups)}</Tooltip>}>
    <span>{formatPercentageNumber(value)}</span>
  </OverlayTrigger>
)

const AffectedTradeFlowsTable: FC<Props> = ({result, groupDetails, reporter, filterEcon}) => {
  const rows: AffectedFlowRow[] = useMemo(() => buildAffectedFlowRows(result), [result])

  const columns: ColumnDef<AffectedFlowRow, any>[] = [
    {
      accessorKey: 'econ',
      header: 'Economy',
      cell: (c) => getNameByISO3(c.getValue()),
      filterFn: 'equalsString',
    },
    {accessorKey: 'hs', header: 'HS code'},
    {accessorKey: 'hsDescription', header: 'Description'},
    {
      accessorKey: 'oldTariff',
      header: 'Current tariff',
      cell: (c) => tariffCell(c.getValue(), c.row.original.oldTariffCode, reporter, groupDetails),
    },
    {
      accessorKey: 'newTariff',
      header: 'New tariff',
      cell: (c) => tariffCell(c.getValue(), c.row.original.newTariffCode, reporter, groupDetails),
    },
    {
      accessorKey: 'currentImportValue',
      header: 'Current import value',
      cell: (c) => shortenLargeNumber(c.getValue()),
    },
    {
      accessorKey: 'tradeLoss',
      header: 'Trade loss',
      cell: (c) => {
        const r = c.row.original
        const tip = lossTooltip(r.tradeLoss, r.tariffEqual, getNameByISO3(r.econ), r.hs)
        const inner = <span className='kt-font-danger'>{shortenLargeNumber(c.getValue())}</span>
        return tip ? (
          <OverlayTrigger overlay={<Tooltip>{tip}</Tooltip>}>
            <span>{inner}</span>
          </OverlayTrigger>
        ) : (
          inner
        )
      },
    },
    {
      accessorKey: 'tradeDiversion',
      header: 'Trade diversion',
      cell: (c) => <span className='kt-font-success'>{shortenLargeNumber(c.getValue())}</span>,
    },
    {
      accessorKey: 'totalLossGain',
      header: 'Total loss/gain',
      cell: (c) => (
        <span className={c.getValue() < 0 ? 'kt-font-danger' : 'kt-font-success'}>
          {shortenLargeNumber(c.getValue())}
        </span>
      ),
    },
    {
      accessorKey: 'pctChange',
      header: '% change',
      cell: (c) => formatPercentageNumber(c.getValue()),
    },
  ]

  const initialColumnFilters: ColumnFiltersState = filterEcon
    ? [{id: 'econ', value: filterEcon}]
    : []

  const csvData = rows.map((r) => ({
    economy: getNameByISO3(r.econ),
    hs_code: r.hs,
    current_tariff: r.oldTariff,
    new_tariff: r.newTariff,
    current_import_value: r.currentImportValue,
    trade_loss: r.tradeLoss,
    trade_diversion: r.tradeDiversion,
    total_loss_gain: r.totalLossGain,
    pct_change: r.pctChange,
  }))

  return (
    <Card className='mb-5' id='pref-loss-flows-table'>
      <Card.Header className='card-header-stretch'>
        <Card.Title className='w-100 d-flex justify-content-between align-items-center'>
          <h3>Affected trade flows</h3>
          <CSVLink data={csvData} filename='preference-loss-affected-flows.csv'>
            <Button variant='outline' size='sm' className='text-gray-700'>
              <i className='bi bi-download me-2'></i>Export to CSV
            </Button>
          </CSVLink>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <DataTable
          data={rows}
          columns={columns}
          enablePagination={rows.length > 50}
          initialSortBy={{id: 'totalLossGain', desc: false}}
          initialColumnFilters={initialColumnFilters}
        />
      </Card.Body>
    </Card>
  )
}

export default AffectedTradeFlowsTable
